import { IMask } from '@utilities/myntr-bundle';

if(!customElements.get('email-form')) {
  class ListrakForm extends HTMLElement {
    constructor() {
      super();
      // simple selectors using custom attributes for easy form component identification
      this._selectors = {
        form: '[js-email-form]', // the form element within the component
        formInner: '.form__inner', // the inner element within the form that holds block content
        subscribeId: '[name="contact[tags]"]', // the value of this input element will be the Listrak subscribe id
        postedSuccessfully: '[name="posted_successfully"]', // this input will exist if we are in the form successfully posted state, otherwise undefined
        errors: '[name="errors"]', // this input will exist if we are in the form errors state, otherwise undefined
        submit: '[js-submit]', // the form submit button of type submit
        onlyOnSuccess: '.form__block--only-on-success', // form elements that should only show on success
        remainOnSuccess: '.form__block--remain-on-success', // form elements that exist normally, but should remain on success state
        requiredCheckbox: '[js-required-checkbox]', // any required checkbox MUST be checked to allow the form to submit, unrelated to Listrak code
        subscriptionPointStart: '[js-listrak-subscription-point-start]', // this should be attached to an input that represents the main subscription point input.
        subscriptionPointField: '[js-listrak-subscription-point-field]', // form inputs that relate to subscription point fields
        subscriptionPointOptIn: '[js-listrak-subscription-point-optin]', // typically attached to a checkbox, this means the attached subscription point of the element requires optin consent
        subscriptionPointEmailInput: '[js-listrak-email-input]', // this will be the email input, and is very important for email sign up
        subscriptionPointPhoneInput: '[js-listrak-phone-input]', // this will be the phone input, and is very important for sms sign up
        formContent: '.form__content' // form elements will be of this class
      };
    }

    // this element may be added to the DOM dynamically, so all initiation code goes here
    async connectedCallback() {
      // gather up our form elements
      this._setElements();
      // Start loading sequence
      this.isSuccessState = this.determineSuccessState();
      // managing loading state
      await this._setComponentState();
    }

    // look for elements based off the selectors list
    _setElements = () => {
      // Maintain a list of all evaluated subscibe Ids
      this.evaluatedSubscribeIds = new Set();
      // grab the main form element
      this.form = this.querySelector(this._selectors.form);
      // the inner content of the form
      this.formInner = this.form?.querySelector(this._selectors.formInner);
      // grab the form element that holds the subscribe id
      this.subscribeIdInput = this.form?.querySelector(this._selectors.subscribeId);
      // if we are in the success state the success input will exist
      this.postedSuccessfully = this.form?.querySelector(this._selectors.postedSuccessfully);
      // if we are in the errors state the errors input will exist
      this.errors = this.form?.querySelector(this._selectors.errors);
      // success content
      this.onlyOnSuccess = this.form?.querySelector(this._selectors.onlyOnSuccess);
      this.remainOnSuccess = this.form?.querySelector(this._selectors.remainOnSuccess);
      // grab the submit button
      this.submitButton = this.form?.querySelector(this._selectors.submit);
      // locate the email input, one should be present on the form
      this.subscriptionPointEmailInput = this.form?.querySelector(this._selectors.subscriptionPointEmailInput);
      // locate a phone input, this may not be present in a given form
      this.subscriptionPointPhoneInput = this.form?.querySelector(this._selectors.subscriptionPointPhoneInput);
      if(this.subscriptionPointPhoneInput) {
        // create a hidden input that holds the value that will be validated and sent, we will shift this.subscriptionPointPhoneInput to be the hidden input
        // the existing this.subscriptionPointPhoneInput will have data stripped and have a mask applied to it
        // only proceed if we have access to the IMask library
        if(typeof IMask !== 'undefined') {
          this.subscriptionPointPhoneInput.classList.add('hidden');
          this.maskedPhoneInput = document.createElement('input');
          this.maskedPhoneInput.classList.add('form__input');
          this.maskedPhoneInput.placeholder = this.subscriptionPointPhoneInput.placeholder;
          this.maskedPhoneInput.autocomplete = 'phone';
          this.maskedPhoneInput.autocapitalize = 'off';
          this.maskedPhoneInput.spellcheck = 'false';
          this.maskedPhoneInput.required = this.subscriptionPointPhoneInput.required;
          this.maskedPhoneInput.type = 'text';
          // require the text input to be numeric, like a telephone number
          this.maskedPhoneInput.inputmode = 'tel';
          // using third party masking service, attach a mask to this input.
          this.PhoneInputMask = IMask(this.maskedPhoneInput, {
            mask: '(000) 000-0000'
          });
          this.subscriptionPointPhoneInput.parentNode.insertBefore(this.maskedPhoneInput, this.subscriptionPointPhoneInput.nextSibling);
          // set up an event listener, on this input's update, update the value of the hidden input.
          this.maskedPhoneInput.addEventListener('input', (event) => {
            this.subscriptionPointPhoneInput.value = this.PhoneInputMask.unmaskedValue
            console.log('subscriptionPointPhoneInput', this.subscriptionPointPhoneInput.value);
            this.subscriptionPointPhoneInput.dispatchEvent(new Event('input'));
          });
        } else {
          console.log('no IMask');
        }
      }
      // locate all elements that represent _ltk.Signup.New() calls
      this.listrakSubscriptionPointStarts = this.form?.querySelectorAll(this._selectors.subscriptionPointStart);
      // locate all elements that will be used to generate _ltk.Signup.SetOptIn() calls
      this.listrakSubscriptionPointOptIns = this.form?.querySelectorAll(this._selectors.subscriptionPointOptIn);
      // locate all elements that represent additional fields to map via _ltk.SignUp.setValue
      this.listrakSubscriptionPointFields = this.form?.querySelectorAll(this._selectors.subscriptionPointField);
      // locate all required checkboxes
      this.requiredCheckboxes = this.form?.querySelectorAll(this._selectors.requiredCheckbox);
    }

    // check if the form is in a success state
    determineSuccessState = () => {
      const recentlySubmittedFormId = sessionStorage.getItem('recently_submitted_listrak_signup');
      const currentFormId = this.form?.id;

      return Boolean(
        this.postedSuccessfully ||
        (recentlySubmittedFormId === currentFormId)
      );
    }

    _setComponentState = async () => {
      try {
        // Check if we're in a success state
        if (this.isSuccessState) {
          // Clear the session storage to prevent this from persisting
          sessionStorage.removeItem('recently_submitted_listrak_signup');

          // Manage form blocks for success state
          this._manageFormBlocks();

          // early exit the component set up, we don't need to do anymore more
          return;
        }

        // If not a success state, proceed with normal initialization
        // Manage form blocks for normal state
        this._manageFormBlocks();

        // Start Listrak subscription point initialization
        this._initializeListrak();

        // set up events
        this._setupEventListeners();

      } catch (error) {
        console.error('Error initializing form:', error);
      }
    }

    _manageFormBlocks = () => {
      const formBlocks = this.formInner?.querySelectorAll('.form__block');

      if (!formBlocks) {
        this.classList.add('blocks-evaluated');
        return;
      }

      formBlocks.forEach(block => {
        const isOnlyOnSuccess = block.classList.contains('form__block--only-on-success');
        const isRemainOnSuccess = block.classList.contains('form__block--remain-on-success');

        if (this.isSuccessState) {
          // On success state:
          // Remove blocks that aren't marked for success state retention
          if (!isOnlyOnSuccess && !isRemainOnSuccess) {
            block.remove();
          }
        } else {
          // On normal state:
          // Remove any success-only blocks
          if (isOnlyOnSuccess) {
            block.remove();
          }
        }
      });

      this.classList.add('blocks-evaluated');
    }

    // set up event listeners for form inputs to manage form state
    _setupEventListeners = () => {
      this.form?.addEventListener('submit', () => this._handleFormSubmit());
      // update form state when email input changes
      this.subscriptionPointEmailInput?.addEventListener('input', () => this._updateSubmitState());
      // update form state when phone input changes (if present)
      this.subscriptionPointPhoneInput?.addEventListener('input', () => this._updateSubmitState());
      // update form state when any required checkbox changes
      this.requiredCheckboxes?.forEach(checkbox => {
        checkbox.addEventListener('change', () => this._updateSubmitState());
      });
      // update form state when any opt-in checkbox changes
      this.listrakSubscriptionPointOptIns?.forEach(optIn => {
        optIn.addEventListener('change', () => this._updateSubmitState());
      });
    }

    _handleFormSubmit = () => {
      /*
        On form submit, we are going to store a session storage key of `recently_submitted_listrak_signup`, with a value of `this.form?.id`
        This should execute before the page redirect occurs.
      */
      sessionStorage.setItem(`recently_submitted_listrak_signup`, `${this.form?.id}`);
    }

    // update form state based on input values and checkbox states
    _updateSubmitState = () => {
      // check if email is valid (non-empty)
      const isEmailValid = this.subscriptionPointEmailInput?.value.trim() !== '';
      // check if all required checkboxes are checked
      const areRequiredCheckboxesChecked = Array.from(this.requiredCheckboxes || []).every(checkbox => checkbox.checked);
      // check phone validity if present and opt-in is checked
      let isPhoneValid = true;
      if (this.subscriptionPointPhoneInput) {
        const phoneOptIn = Array.from(this.listrakSubscriptionPointOptIns).find(optIn =>
          optIn.dataset.listrakSubscribeId === this.subscriptionPointPhoneInput.dataset.listrakSubscribeId
        );
        if (phoneOptIn && phoneOptIn.checked) {
          isPhoneValid = this.subscriptionPointPhoneInput.value.trim() !== '';
        }
        if (this.subscriptionPointPhoneInput.required) {
          isPhoneValid = this.subscriptionPointPhoneInput.value.trim() !== '';
        }
      }
      // determine overall form validity
      const isFormValid = isEmailValid && areRequiredCheckboxesChecked && isPhoneValid;
      // update submit button state based on form validity
      if (isFormValid) {
        this.submitButton.removeAttribute('disabled');
        this.submitButton.setAttribute('aria-disabled', 'false');
      } else {
        this.submitButton.setAttribute('disabled', '');
        this.submitButton.setAttribute('aria-disabled', 'true');
      }
    }

    // Listrak initialize code taken from Listrak Subscription Point documentation
    _initializeListrak = () => {
      if (typeof _ltk === "undefined") {
        if (document.addEventListener) {
          document.addEventListener("ltkAsyncListener", this._setListrakSubscriptionPoint);
        } else {
          const e = document.documentElement;
          e.ltkAsyncProperty = 0;
          e.attachEvent("onpropertychange", (event) => {
            if (event.propertyName === "ltkAsyncProperty") {
              this._setListrakSubscriptionPoint();
            }
          });
        }
      } else {
        this._setListrakSubscriptionPoint();
      }
    }

    // Set up the _ltk.SignUp functions
    _setListrakSubscriptionPoint = () => {
      try {
        this._setLTK();
      } catch (error) {
        console.error('Error setting Listrak subscription point:', error);
      }
    }

    _setLTK = () => {
      // First, evaluate opt-in subscription points that aren't the main subscription point
      this.listrakSubscriptionPointOptIns.forEach((optIn) => {
        const optInSubscribeId = optIn.dataset.listrakSubscribeId;
        // Skip if we've already processed this subscription point
        if (this.evaluatedSubscribeIds.has(optInSubscribeId)) return;

        const isSmS = optInSubscribeId.endsWith('SMS');
        const inputId = isSmS ? this.subscriptionPointPhoneInput.id : this.subscriptionPointEmailInput.id;
        const channelType = isSmS ? 'sms' : 'email';

        _ltk.Signup.New(optInSubscribeId, inputId, _ltk.Signup.TYPE.CLICK, this.submitButton.id, channelType);
        _ltk.Signup.SetValue(optInSubscribeId, 'ltkOptIn', 'off');
        _ltk.Signup.SetOptIn(optInSubscribeId, optIn.id);

        this.evaluatedSubscribeIds.add(optInSubscribeId);
      });

      // Finally, process the main subscription points last
      this.listrakSubscriptionPointStarts.forEach((startPoint) => {
        const subscribeId = startPoint.dataset.listrakSubscribeId;
        const channelType = subscribeId.includes('SMS') ? 'sms' : 'email';

        // Process fields for this subscription point first
        this.listrakSubscriptionPointFields.forEach((field) => {
          if (field.dataset.listrakSubscribeId === subscribeId) {
            _ltk.Signup.SetField(subscribeId, field.id, { key: field.dataset.listrakInputAttributeId });
          }
        });

        // Process opt-ins for this subscription point
        this.listrakSubscriptionPointOptIns.forEach((optIn) => {
          if (optIn.dataset.listrakSubscribeId === subscribeId) {
            _ltk.Signup.SetValue(subscribeId, 'ltkOptIn', 'off');
            _ltk.Signup.SetOptIn(subscribeId, optIn.id);
          }
        });

        // Set up the main subscription point last
        _ltk.Signup.New(subscribeId, startPoint.id, _ltk.Signup.TYPE.CLICK, this.submitButton.id, channelType);
        this.evaluatedSubscribeIds.add(subscribeId);
      });

      console.log('Listrak subscription points set up successfully', this);
      // Set initial form state after Listrak setup
      this._updateSubmitState();
    }
  }

  customElements.define('email-form', ListrakForm);
}
